import React, { useState } from "react";
import {
  Alert,
  Button,
  Card,
  FormControl,
  FormLabel,
  Input,
  Option,
  Select,
  Stack,
  Typography,
} from "@mui/joy";
import type { SalesDeskData, SalesCommand } from "../../../types/salesDesk";
export function SalesSettingsPanel({
  data,
  command,
  busy,
}: {
  data: SalesDeskData;
  command: SalesCommand;
  busy: boolean;
}) {
  return (
    <Settings
      key={data.policy.revision}
      data={data}
      command={command}
      busy={busy}
    />
  );
}
function Settings({
  data,
  command,
  busy,
}: {
  data: SalesDeskData;
  command: SalesCommand;
  busy: boolean;
}) {
  const [response, setResponse] = useState(
      String(data.policy.response_minutes),
    ),
    [escalate, setEscalate] = useState(String(data.policy.escalation_minutes)),
    [manager, setManager] = useState(
      data.policy.manager ? String(data.policy.manager) : "",
    ),
    [quotas, setQuotas] = useState(data.policy.monthly_quotas),
    [error, setError] = useState("");
  const save = async () => {
    try {
      await command("policy", {
        revision: data.policy.revision,
        response_minutes: Number(response),
        escalation_minutes: Number(escalate),
        manager: manager ? Number(manager) : null,
        monthly_quotas: Object.fromEntries(
          Object.entries(quotas).filter(([, v]) => v !== ""),
        ),
      });
    } catch (e) {
      setError((e as Error).message);
    }
  };
  return (
    <Card sx={{ maxWidth: 850 }}>
      <Stack spacing={2}>
        <Typography level="h3">Response targets and rep goals</Typography>
        <Typography>
          Targets use elapsed time. Manager notifications are deduplicated for
          each outstanding response and checked every five minutes.
        </Typography>
        {error && <Alert color="danger">{error}</Alert>}
        <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
          <FormControl sx={{ flex: 1 }}>
            <FormLabel>First-response target (minutes)</FormLabel>
            <Input
              type="number"
              value={response}
              onChange={(e) => setResponse(e.target.value)}
            />
          </FormControl>
          <FormControl sx={{ flex: 1 }}>
            <FormLabel>Escalate after (minutes)</FormLabel>
            <Input
              type="number"
              value={escalate}
              onChange={(e) => setEscalate(e.target.value)}
            />
          </FormControl>
        </Stack>
        <FormControl>
          <FormLabel>Escalation manager</FormLabel>
          <Select value={manager} onChange={(_, v) => setManager(v || "")}>
            <Option value="">No automatic manager notifications</Option>
            {data.members.map((m) => (
              <Option key={m.id} value={String(m.id)}>
                {m.name}
              </Option>
            ))}
          </Select>
        </FormControl>
        <Typography level="body-sm">
          The manager must have access to all contacts and Inbox. The worklist
          still highlights overdue leads without automatic notifications.
        </Typography>
        <Typography level="title-lg">Monthly won-value targets</Typography>
        <Typography level="body-sm">
          Sales Reports compares won opportunity value to these targets,
          prorated by calendar day for the selected period.
        </Typography>
        {data.members.map((m) => (
          <FormControl key={m.id}>
            <FormLabel>{m.name} monthly target</FormLabel>
            <Input
              type="number"
              startDecorator="$"
              value={quotas[String(m.id)] || ""}
              placeholder="No target"
              onChange={(e) =>
                setQuotas({ ...quotas, [String(m.id)]: e.target.value })
              }
              slotProps={{ input: { min: 0, step: ".01" } }}
            />
          </FormControl>
        ))}
        <Button loading={busy} onClick={() => void save()}>
          Save team settings
        </Button>
      </Stack>
    </Card>
  );
}
